import React, { useState, useMemo } from "react";
import { Box } from "@mui/material";
import { Check as CheckIcon, Close as CloseIcon } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from "ag-grid-community";
import { Alert, DataTableV2, useAgGridOptions } from "@platform/design-system";
import { useApi } from "@platform/api";

import { Loader } from "../../../components";
import { AnalystUser } from "../types";

import { AnalystForm } from "./AnalystForm";
import { EditActionCellRenderer } from "./EditActionCellRenderer";

export const AnalystsTab: React.FC = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingAnalyst, setEditingAnalyst] = useState<AnalystUser | null>(
    null,
  );

  const axiosInstance = useApi();

  const {
    data: analysts = [],
    isLoading,
    error,
    refetch,
  } = useQuery<AnalystUser[], Error>({
    queryKey: ["analysts"],
    queryFn: async () => {
      const response = await axiosInstance.get<AnalystUser[]>(
        "brokerportal/v1/analysts",
      );
      return response.data;
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { resetPage, ...dataTableOptions } = useAgGridOptions({});

  const columnDefs = useMemo<ColDef<AnalystUser>[]>(
    () => [
      { field: "email", headerName: "Email" },
      { field: "givenName", headerName: "First Name" },
      { field: "familyName", headerName: "Last Name" },
      {
        field: "activeRfpCount",
        headerName: "Active RFPs",
        valueFormatter: (params: ValueFormatterParams<AnalystUser>) =>
          String(params.value ?? 0),
      },
      {
        field: "blocked",
        headerName: "Status",
        cellRenderer: (params: ICellRendererParams<AnalystUser>) =>
          params.value ? (
            <CloseIcon fontSize="small" sx={{ color: "error.main" }} />
          ) : (
            <CheckIcon fontSize="small" sx={{ color: "success.main" }} />
          ),
      },
      {
        field: "allowRfpAssignment",
        headerName: "Can Assign RFPs",
        cellRenderer: (params: ICellRendererParams<AnalystUser>) =>
          params.value ? (
            <CheckIcon fontSize="small" sx={{ color: "success.main" }} />
          ) : (
            <CloseIcon fontSize="small" sx={{ color: "error.main" }} />
          ),
      },
      { field: "teamName", headerName: "Team Name" },
      {
        field: "title",
        headerName: "Title",
        valueFormatter: (params: ValueFormatterParams<AnalystUser>) =>
          params.value
            ? String(params.value)
                .replaceAll("_", " ")
                .replace(/\b\w/g, (char: string) => char.toUpperCase())
            : "-",
      },
      {
        field: "totalAssignedRfpHours",
        headerName: "Assigned RFP Points",
        valueFormatter: (params: ValueFormatterParams<AnalystUser>) =>
          params.value != null ? Number(params.value).toFixed(2) : "-",
      },
      {
        field: "totalCapacityHours",
        headerName: "Total Capacity",
        valueFormatter: (params: ValueFormatterParams<AnalystUser>) =>
          params.value != null ? String(params.value) : "-",
      },
      {
        field: "startDate",
        headerName: "Start Date",
        valueFormatter: (params: ValueFormatterParams<AnalystUser>) =>
          params.value
            ? new Date(params.value as string).toLocaleDateString()
            : "-",
      },
      {
        headerName: "Actions",
        cellRenderer: EditActionCellRenderer,
        sortable: false,
        filter: false,
        maxWidth: 100,
      },
    ],
    [],
  );

  const handleEditAnalyst = (analyst: AnalystUser): void => {
    setEditingAnalyst(analyst);
    setIsFormOpen(true);
  };

  const handleCloseForm = (): void => {
    setIsFormOpen(false);
    setEditingAnalyst(null);
  };

  const handleFormSuccess = (): void => {
    void refetch();
    handleCloseForm();
  };

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert severity="error">
          Failed to load analysts:{" "}
          {error instanceof Error ? error.message : "Unknown error"}
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <DataTableV2
        rowData={analysts}
        columnDefs={columnDefs}
        context={{ onEditAnalyst: handleEditAnalyst }}
        {...dataTableOptions}
      />

      {editingAnalyst && (
        <AnalystForm
          open={isFormOpen}
          onClose={handleCloseForm}
          onSuccess={handleFormSuccess}
          analyst={editingAnalyst}
        />
      )}
    </>
  );
};
